import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CACHE_TIME } from '@hygge/shared';
import {
  getAutomations,
  createAutomation,
  updateAutomation,
  deleteAutomation,
  getAutomationRuns,
  testAutomation,
  getAutomationRecipes,
  generateAutomation,
} from '@/lib/automations';
import type { UpsertAutomation, GenerateAutomationResult } from '@/lib/automations';
import type { AutomationRule } from '@hygge/shared';

export type { GenerateAutomationResult };

// ─── Query keys ─────────────────────────────────────────────────────────

export const automationKeys = {
  all: ['automations'] as const,
  lists: () => [...automationKeys.all, 'list'] as const,
  list: (boardId: string) => [...automationKeys.lists(), boardId] as const,
  runs: (id: string) => [...automationKeys.all, 'runs', id] as const,
  recipes: () => [...automationKeys.all, 'recipes'] as const,
};

// ─── Queries ─────────────────────────────────────────────────────────────

export function useAutomations(boardId: string | undefined) {
  return useQuery({
    queryKey: automationKeys.list(boardId ?? ''),
    queryFn: () => getAutomations(boardId!),
    enabled: !!boardId,
    staleTime: CACHE_TIME.SHORT,
  });
}

export function useAutomationRuns(id: string | null) {
  return useQuery({
    queryKey: automationKeys.runs(id ?? ''),
    queryFn: () => getAutomationRuns(id!),
    enabled: !!id,
    staleTime: CACHE_TIME.SHORT,
  });
}

export function useAutomationRecipes() {
  return useQuery({
    queryKey: automationKeys.recipes(),
    queryFn: getAutomationRecipes,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────

export function useCreateAutomation(boardId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (body: UpsertAutomation) => createAutomation(boardId!, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: automationKeys.list(boardId ?? '') });
    },
  });
}

export function useUpdateAutomation(boardId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<UpsertAutomation> }) =>
      updateAutomation(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: automationKeys.list(boardId ?? '') });
    },
  });
}

export function useDeleteAutomation(boardId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteAutomation(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: automationKeys.list(boardId ?? '') });
    },
  });
}

export function useTestAutomation() {
  return useMutation({
    mutationFn: ({ id, taskKey }: { id: string; taskKey: string }) => testAutomation(id, taskKey),
  });
}

export function useGenerateAutomation(boardId: string | undefined) {
  return useMutation({
    mutationFn: (body: { prompt: string; currentRule?: AutomationRule }) =>
      generateAutomation(boardId!, body),
  });
}
